import * as React from 'react';
import ReactDOM from 'react-dom';
import { Router, Route, Switch } from 'react-router-dom';
import '@mdi/font/css/materialdesignicons.min.css';

import withAsync from './components/withAsync';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import FocusOnLoad from './components/FocusOnLoad/FocusOnLoad';
import { createBrowserHistory as createHistory } from 'history';
import {
	ClientColorContext,
	NotificationSnackbar,
	MetadataContext,
	ThemeContext,
	TitleContext as TitleProvider,
	TooltipContext,
	FacultyXPViewContext,
} from '@dm/components';
import { StyleContextProvider } from '@dm/components/lib/Style/Style';
import focusinHandler from './utils/focusin-handler';
import FacultyExperienceSwitcher from './components/FacultyExperienceSwitcher/FacultyExperienceSwitcher';
import { load } from './components/FacultyExperienceSwitcher/switcherStorage';

// Set up history for both Backbone and React routers
const history = createHistory();

const PropertiesApp = withAsync(
	() => import('./packages/@dm/properties/BaseApp'),
	'@dm/properties'
);
const ReportStylesApp = withAsync(
	() => import('./packages/@dm/report-styles/BaseApp'),
	'@dm/report-styles'
);
const CourseEvalApp = withAsync(
	() => import('./packages/@dm/course-eval/BaseApp'),
	'@dm/course-eval'
);
const NavbarApp = withAsync(
	() => import('./packages/@dm/navbar/BaseApp'),
	'@dm/navbar'
);
const NavigationApp = withAsync(
	() => import('./packages/@dm/navigation/BaseApp'),
	'@dm/navigation'
);
const ManageDataApp = withAsync(
	() => import('./packages/@dm/managedata/BaseApp'),
	'@dm/managedata'
);
const SensorApp = withAsync(
	() => import('./packages/@dm/sensor/BaseApp'),
	'@dm/sensor'
);
const SiteMapApp = withAsync(
	() => import('./packages/@dm/sitemap/BaseApp'),
	'@dm/sitemap'
);
const ActivitiesApp = withAsync(
	() => import('./packages/@dm/activities/BaseApp'),
	'@dm/activities'
);
const WorkflowApp = withAsync(
	() => import('./packages/dm-spu-workflow/BaseApp'),
	'dm-spu-workflow'
);
const WorkflowAdminToolApp = withAsync(
	() => import('./packages/@dm/wat-react/BaseApp'),
	'@dm/wat-react'
);
const CVUploaderApp = withAsync(
	() => import('./packages/@dm/cv-uploader/BaseApp'),
	'@dm/cv-uploader'
);
const InstrumentManagementPreviewApp = withAsync(
	() => import('./packages/@dm/instrument-management-preview/BaseApp'),
	'@dm/instrument-management-preview'
);
const ActivitiesToolsApp = withAsync(
	() => import('./packages/@dm/activities-tools/BaseApp'),
	'@dm/activities-tools'
);
const ReportBuilderApp = withAsync(
	() => import('./packages/@dm/report-builder/BaseApp'),
	'@dm/report-builder'
);
const ReportingApp = withAsync(
	() => import('./packages/@dm/reporting/BaseApp'),
	'@dm/reporting'
);
const CareerManagementApp = withAsync(
	() => import('./packages/@dm/career-management/BaseApp'),
	'@dm/career-management'
);

const NavBar = function({ el, metadata }) {
	if (el) {
		return ReactDOM.createPortal(
			<FacultyExperienceSwitcher
				metadata={metadata}
				NavbarApp={NavbarApp}
				NavigationApp={NavigationApp}
			/>,
			el
		);
	} else {
		return null;
	}
};

export default class App extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			bannerEl: null,
		};
	}
	componentDidMount() {
		this.focusOnLoad();
		this.setState({
			bannerEl: document.getElementById('banner'),
		});
		// focusin
		document.addEventListener('focusin', focusinHandler, false);
	}
	componentWillUnmount() {
		document.removeEventListener('focusin', focusinHandler);
	}

	focusOnLoad = () => {
		const main =
			document.getElementsByTagName('main')[0] ||
			document.getElementById('content') ||
			document.getElementById('maincontent');
		if (main) {
			main.tabIndex = -1;
			main.style.outline = 'none';
			main.focus();
			window.scrollTo(0, 0);
		}
	};

	renderApp = (App, props) => (
		<TitleProvider baseTitle={this.props.metadata.documentTitleBase}>
			<ScrollToTop>
				<App metadata={this.props.metadata} {...props} />
			</ScrollToTop>
		</TitleProvider>
	);

	renderProperties = (props) => this.renderApp(PropertiesApp, props);
	renderReportStyles = (props) => this.renderApp(ReportStylesApp, props);
	renderCourseEvalManagement = (props) => this.renderApp(CourseEvalApp, props);
	renderManageData = (props) => this.renderApp(ManageDataApp, props);
	renderSensor = (props) => this.renderApp(SensorApp, props);
	renderSitemap = (props) => this.renderApp(SiteMapApp, props);
	renderActivities = (props) => this.renderApp(ActivitiesApp, props);
	renderInstrumentManagementPreview = (props) =>
		this.renderApp(InstrumentManagementPreviewApp, props);
	renderActivitiesToolsPreview = (props) =>
		this.renderApp(ActivitiesToolsApp, props);
	renderCVUploader = (props) => this.renderApp(CVUploaderApp, props);
	renderReportBuilder = (props) => this.renderApp(ReportBuilderApp, props);
	renderReporting = (props) => this.renderApp(ReportingApp, props);
	renderWorkflow = (props) => this.renderApp(WorkflowApp, props);
	renderWorkflowAdminTool = (props) =>
		this.renderApp(WorkflowAdminToolApp, props);
	renderCareerManagement = (props) =>
		this.renderApp(CareerManagementApp, props);

	render() {
		const {
			linkColorPrimary,
			linkColorSecondary,
			trimColorPrimary,
			trimColorSecondary,
		} = this.props.metadata;
		const switcherState = load(this.props.metadata.httpSessionId);
		return (
			<Router history={history}>
				<link
					href="https://cdn.watermarkinsights.com/css/1.5.0/tokens.css"
					rel="stylesheet"
				/>
				<NotificationSnackbar>
					<FocusOnLoad>
						<FacultyXPViewContext.Provider value={switcherState.isNew}>
							<StyleContextProvider>
								<MetadataContext.Provider value={this.props.metadata}>
									<ClientColorContext.Provider
										value={{
											linkColorPrimary,
											linkColorSecondary,
											trimColorPrimary,
											trimColorSecondary,
										}}
									>
										<ThemeContext.Provider value="ripple">
											<NavBar
												el={this.state.bannerEl}
												metadata={this.props.metadata}
											/>
											<Switch>
												<Route
													path="*/app/admin/properties/"
													render={this.renderProperties}
												/>
												<Route
													path="*/app/import/cv"
													render={this.renderCVUploader}
												/>
												<Route
													path="*/app/course-eval"
													render={this.renderCourseEvalManagement}
												/>
												<Route
													path="*/app/managedata"
													render={this.renderManageData}
												/>
												<Route path="*/app/sensor" render={this.renderSensor} />
												<Route
													path="*/app/sitemap"
													render={this.renderSitemap}
												/>
												<Route
													path="*/app/activities/tools/(instruments)?/:instrumentId?/(screens)?/:screenId?"
													render={this.renderActivitiesToolsPreview}
												/>
												<Route
													path="*/app/activities/add/(instruments)?/:instrumentId?/"
													render={this.renderActivities}
												/>
												<Route
													path="*/app/activities/(users)?/:userId?/instruments/:instrumentId?/(screens)?/:screenId?/"
													render={this.renderActivities}
												/>
												<Route
													path="*/app/settings/instruments/:instrumentId"
													render={this.renderActivities}
												/>
												<Route
													path="*/app/home/instruments/:instrumentId"
													render={this.renderActivities}
												/>
												<Route
													path="*/app/workflow/adminTool"
													render={this.renderWorkflowAdminTool}
												/>
												<Route
													path={['*/app/workflow', '*/xapp/workflow']}
													render={this.renderWorkflow}
												/>
												<Route
													path="*/app/reporting/ssr"
													render={this.renderReportBuilder}
												/>
												<Route
													path="*/app/reporting/styles/"
													render={this.renderReportStyles}
												/>
												<Route
													path="*/app/reporting/"
													render={this.renderReporting}
												/>
												<Route
													path="*/app/cm"
													render={this.renderCareerManagement}
												/>
												<Route
													path="*/app"
													component={withAsync(
														() => import('./components/NoMatch'),
														'NoMatch'
													)}
												/>
												<Route
													path="*/instrument/management/showManagement"
													render={this.renderInstrumentManagementPreview}
												/>
											</Switch>
											<TooltipContext />
										</ThemeContext.Provider>
									</ClientColorContext.Provider>
								</MetadataContext.Provider>
							</StyleContextProvider>
						</FacultyXPViewContext.Provider>
					</FocusOnLoad>
				</NotificationSnackbar>
			</Router>
		);
	}
}
